import { createContext, ReactNode, useCallback, useContext, useState } from "react";

type DocumentToPreview = {
  id: string;
  name: string;
};

type DocumentPreviewContextType = {
  documentToPreview: DocumentToPreview | undefined;
  setDocumentToPreview: (document: DocumentToPreview | undefined) => void;
  openDocumentPreviewModal: boolean;
  setOpenDocumentPreviewModal: (open: boolean) => void;
  handleOpenDocumentPreviewModal: (open: boolean) => void;
  handlePreviewDocument: (document: DocumentToPreview) => void;
};

const DocumentPreviewContext = createContext<DocumentPreviewContextType | undefined>(undefined);

type DocumentPreviewProviderProps = {
  children: ReactNode;
};

export function DocumentPreviewProvider({ children }: DocumentPreviewProviderProps) {
  const [documentToPreview, setDocumentToPreview] = useState<DocumentToPreview | undefined>(
    undefined
  );
  const [openDocumentPreviewModal, setOpenDocumentPreviewModal] = useState<boolean>(false);

  const handleOpenDocumentPreviewModal = useCallback((open: boolean) => {
    if (!open) {
      setDocumentToPreview(undefined);
      setOpenDocumentPreviewModal(false);
    } else {
      setOpenDocumentPreviewModal(true);
    }
  }, []);

  const handlePreviewDocument = useCallback((document: DocumentToPreview) => {
    setDocumentToPreview(document);
    setOpenDocumentPreviewModal(true);
  }, []);

  return (
    <DocumentPreviewContext.Provider
      value={{
        documentToPreview,
        setDocumentToPreview,
        openDocumentPreviewModal,
        setOpenDocumentPreviewModal,
        handleOpenDocumentPreviewModal,
        handlePreviewDocument,
      }}
    >
      {children}
    </DocumentPreviewContext.Provider>
  );
}

export function useDocumentPreview(): DocumentPreviewContextType {
  const context = useContext(DocumentPreviewContext);
  if (context === undefined) {
    throw new Error("useDocumentPreview must be used within a DocumentPreviewProvider");
  }
  return context;
}
